// src/components/NavLinks.tsx
'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { ChevronDown, Menu } from 'lucide-react'
import { useI18n } from '@/components/I18nProvider'

interface NavLinksProps {
  isLoggedIn: boolean
  // Whether the viewer can access the ACT product (Pro/comp). When true AND the
  // current route is under /act, the top bar swaps to an ACT-targeted app nav
  // that mirrors the SAT nav's structure — so ACT feels like the same product,
  // not a bolt-on with breadcrumb back-links. Defaults false (SAT nav).
  isPaid?: boolean
}

/**
 * Top-bar links. The bar now shows two distinct sets:
 *
 *  - Logged IN  → a lean APP nav (Dashboard, Study Plan, Practice, Tests ▾,
 *    Learn ▾, Tutor). Learn is a disclosure grouping the knowledge surfaces
 *    (Lessons, Flashcard Review, Blog). Account / social entries (Groups,
 *    Leaderboard, Badges, Invite, Settings) moved into the avatar dropdown
 *    (see user-menu.tsx), and the marketing links (About, Pricing) drop out —
 *    a studying user doesn't need them in the chrome.
 *  - Logged OUT → a MARKETING nav (Practice/Tests/Learn tasters + Pricing, Blog,
 *    About). No Dashboard/Study Plan/Tutor, which would just bounce a visitor to
 *    sign-in.
 *
 * The Tests dropdown is shared by both (it gates Bookmarks on isLoggedIn).
 */
export default function NavLinks({ isLoggedIn, isPaid = false }: NavLinksProps) {
  const { t } = useI18n()
  const pathname = usePathname()
  // Section-aware: on any /act route, a paid user gets the ACT app nav instead
  // of the SAT one. Same shape (3 flat links + Tests ▾ + Learn ▾), ACT targets.
  const isAct = !!pathname && pathname.startsWith('/act')
  const actNav = isLoggedIn && isPaid && isAct
  const [testsOpen, setTestsOpen] = useState(false)
  const [learnOpen, setLearnOpen] = useState(false)
  const [aboutOpen, setAboutOpen] = useState(false)
  // Mobile hamburger: below `md` the inline links can't fit a phone's width, so
  // they collapse into this menu. Without it the row overflowed the viewport,
  // which pushed `position: fixed` chrome (the Sage FAB, the avatar menu) off to
  // the bottom-right of the widened layout viewport — you had to scroll to find
  // them. Collapsing the links keeps the bar within the viewport.
  const [menuOpen, setMenuOpen] = useState(false)
  const testsRef = useRef<HTMLDivElement>(null)
  const learnRef = useRef<HTMLDivElement>(null)
  const aboutRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close dropdowns on outside click + Escape (keyboard users need a way to
  // dismiss without tabbing all the way through the panel).
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (testsRef.current && !testsRef.current.contains(e.target as Node)) {
        setTestsOpen(false)
      }
      if (learnRef.current && !learnRef.current.contains(e.target as Node)) {
        setLearnOpen(false)
      }
      if (aboutRef.current && !aboutRef.current.contains(e.target as Node)) {
        setAboutOpen(false)
      }
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setTestsOpen(false)
        setLearnOpen(false)
        setAboutOpen(false)
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [])

  const linkClass = 'shrink-0 text-white/90 hover:text-white transition-colors'
  // Dropdown items live inside .nav-gradient but the panel opts out of the
  // gradient's white-text cascade (see globals.css [data-nav-popover]).
  // Hover uses accent-foreground (theme-aware) so the text stays readable
  // on bg-muted in BOTH light mode (slate-100 surface) and dark mode.
  const dropdownItemClass =
    'block px-3 py-2 text-sm text-foreground hover:bg-muted hover:text-accent-foreground rounded transition-colors'

  // The SAT/marketing nav has NO Tests or Learn dropdown. Both are plain links
  // to real hub pages (/tests, /learn), so every top-level item in the bar
  // behaves the same way — previously "Tests" looked like Study Plan and
  // Practice but opened a menu, and the page actually called Tests was reachable
  // only via an "All Tests" row inside that menu.
  //
  // What the Tests menu used to hold, and where it went:
  //   Checkpoint / Full-length SAT  → the /tests hub, as its real actions
  //   PSAT / ACT / AP ("Soon")      → one line on /tests; nav is not the place
  //                                   to advertise things that do not exist yet
  //   College Score Targets         → /tests (it answers "what should I aim for")
  //   Bookmarks                     → the avatar menu, with the other personal
  //                                   surfaces (see user-menu.tsx)
  //
  // The ACT nav below KEEPS its dropdown: its four entries are all live
  // destinations and there is no /act/tests hub to point at.

  // ── ACT app nav (mirrors the SAT Tests/Learn dropdowns, ACT targets) ──
  // Reuses testsOpen/learnOpen + refs since only one nav renders at a time.
  const actTestsDropdown = (
    <div className="relative" ref={testsRef}>
      <button
        onClick={() => setTestsOpen((v) => !v)}
        className={`${linkClass} flex items-center gap-1`}
        aria-expanded={testsOpen}
        aria-haspopup="true"
      >
        Tests
        <ChevronDown
          className={`h-3 w-3 transition-transform ${testsOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      {testsOpen && (
        <div
          data-nav-popover
          className="absolute left-0 top-full mt-2 w-56 rounded-md border border-border bg-card p-1 shadow-lg z-50"
        >
          <Link
            href="/act/checkpoint"
            className={dropdownItemClass}
            onClick={() => setTestsOpen(false)}
          >
            Checkpoint (1 hr)
          </Link>
          <Link href="/act/test" className={dropdownItemClass} onClick={() => setTestsOpen(false)}>
            Full-length ACT
          </Link>
          <Link href="/act/drill" className={dropdownItemClass} onClick={() => setTestsOpen(false)}>
            Quick drill
          </Link>
          <Link
            href="/act/writing"
            className={dropdownItemClass}
            onClick={() => setTestsOpen(false)}
          >
            Writing
          </Link>
        </div>
      )}
    </div>
  )
  const actLearnDropdown = (
    <div className="relative" ref={learnRef}>
      <button
        onClick={() => setLearnOpen((v) => !v)}
        className={`${linkClass} flex items-center gap-1`}
        aria-expanded={learnOpen}
        aria-haspopup="true"
      >
        {t('nav.learn')}
        <ChevronDown
          className={`h-3 w-3 transition-transform ${learnOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      {learnOpen && (
        <div
          data-nav-popover
          className="absolute left-0 top-full mt-2 w-48 rounded-md border border-border bg-card p-1 shadow-lg z-50"
        >
          <Link href="/act/learn" className={dropdownItemClass} onClick={() => setLearnOpen(false)}>
            Lessons
          </Link>
          <Link
            href="/act/foundations"
            className={dropdownItemClass}
            onClick={() => setLearnOpen(false)}
          >
            Foundations
          </Link>
          <Link
            href="/act/review"
            className={dropdownItemClass}
            onClick={() => setLearnOpen(false)}
          >
            Review
          </Link>
          <Link
            href="/act/bookmarks"
            className={dropdownItemClass}
            onClick={() => setLearnOpen(false)}
          >
            Bookmarks
          </Link>
          <Link
            href="/act/mastery"
            className={dropdownItemClass}
            onClick={() => setLearnOpen(false)}
          >
            Mastery Map
          </Link>
        </div>
      )}
    </div>
  )
  const actMobileLinks = [
    { href: '/act/dashboard', label: t('nav.dashboard') },
    { href: '/act/study-plan', label: 'Study Plan' },
    { href: '/act/practice', label: t('nav.practice') },
    { href: '/act/checkpoint', label: 'Checkpoint (1 hr)' },
    { href: '/act/test', label: 'Full-length ACT' },
    { href: '/act/drill', label: 'Quick drill' },
    { href: '/act/writing', label: 'Writing' },
    { href: '/act/learn', label: 'Lessons' },
    { href: '/act/foundations', label: 'Foundations' },
    { href: '/act/review', label: 'Review' },
    { href: '/act/bookmarks', label: 'Bookmarks' },
    { href: '/act/mastery', label: 'Mastery Map' },
  ]

  // Flat link list for the mobile hamburger — mirrors the desktop nav exactly.
  // It used to also flatten in the Tests dropdown's rows, because the panel
  // can't nest sub-menus. With Tests and Learn now pointing at real hub pages,
  // those rows live on the hub instead and the phone menu is the desktop nav.
  const mobileLinks = isLoggedIn
    ? [
        { href: '/dashboard', label: t('nav.dashboard') },
        { href: '/study-plan', label: 'Study Plan' },
        { href: '/practice', label: t('nav.practice') },
        { href: '/learn', label: t('nav.learn') },
        { href: '/videos', label: t('nav.videos') },
        { href: '/blog', label: 'Blog' },
        { href: '/sat', label: 'Tests' },
        { href: '/tutor', label: 'Sage' },
      ]
    : [
        { href: '/practice', label: t('nav.practice') },
        { href: '/learn', label: t('nav.learn') },
        { href: '/videos', label: t('nav.videos') },
        { href: '/pricing', label: t('nav.pricing') },
        { href: '/blog', label: 'Blog' },
        { href: '/tests', label: 'Tests' },
        { href: '/guarantee', label: 'Score guarantee' },
        { href: '/proof', label: 'Student results' },
        // On phones the header hides the "Sign in" pill (only the "Sign up
        // free" CTA fits the h-14 bar at 375px) — this entry keeps sign-in
        // one tap away for returning users.
        { href: '/signin', label: 'Sign in' },
      ]

  return (
    <nav className="flex items-center">
      {/* Desktop inline nav (md+). Hidden on phones, where the full link row
          overflowed the viewport and pushed the fixed Sage FAB + avatar menu
          off-screen; on mobile those links live in the hamburger menu below. */}
      <div className="hidden items-center gap-3 text-xs sm:gap-4 sm:text-sm md:flex">
        {actNav ? (
          <>
            {/* ACT app nav — same shape as the SAT app nav, ACT targets. */}
            <Link href="/act/dashboard" className={linkClass}>
              {t('nav.dashboard')}
            </Link>
            <Link href="/act/study-plan" className={linkClass}>
              Study Plan
            </Link>
            <Link href="/act/practice" className={linkClass}>
              {t('nav.practice')}
            </Link>
            {actTestsDropdown}
            {actLearnDropdown}
          </>
        ) : isLoggedIn ? (
          <>
            {/* APP nav — lean. Account/social links live in the avatar menu. */}
            <Link href="/dashboard" className={linkClass}>
              {t('nav.dashboard')}
            </Link>
            <Link href="/study-plan" className={linkClass}>
              Study Plan
            </Link>
            <Link href="/practice" className={linkClass}>
              {t('nav.practice')}
            </Link>
            {/* Signed IN, Tests goes to /sat, not /tests: that page has the
                resume-in-progress, sitting history and plan-aware start
                buttons, none of which /tests can show. /tests stays the
                signed-OUT landing page — it is indexed, and sending a visitor
                to /sat would bounce them straight into /signin. */}
            <Link href="/sat" className={linkClass}>
              Tests
            </Link>
            <Link href="/learn" className={linkClass}>
              {t('nav.learn')}
            </Link>
            {/* The video library is a Learn surface (every video is keyed to a lesson's
                topic), so it sits beside Learn rather than getting a dashboard tile —
                Learn itself has no dashboard entry either; the nav IS the entry. */}
            <Link href="/videos" className={linkClass}>
              {t('nav.videos')}
            </Link>
            <Link href="/tutor" className={linkClass}>
              Sage
            </Link>
          </>
        ) : (
          <>
            {/* MARKETING nav — for logged-out visitors. */}
            <Link href="/practice" className={linkClass}>
              {t('nav.practice')}
            </Link>
            <Link href="/tests" className={linkClass}>
              Tests
            </Link>
            <Link href="/learn" className={linkClass}>
              {t('nav.learn')}
            </Link>
            <Link href="/videos" className={linkClass}>
              {t('nav.videos')}
            </Link>
            <Link href="/pricing" className={linkClass}>
              {t('nav.pricing')}
            </Link>
            <Link href="/blog" className={linkClass}>
              Blog
            </Link>

            {/* About dropdown — marketing only */}
            <div className="relative" ref={aboutRef}>
              <button
                onClick={() => setAboutOpen((v) => !v)}
                className={`${linkClass} flex items-center gap-1`}
                aria-expanded={aboutOpen}
                aria-haspopup="true"
              >
                About
                <ChevronDown
                  className={`h-3 w-3 transition-transform ${aboutOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {aboutOpen && (
                <div
                  data-nav-popover
                  className="absolute left-0 top-full mt-2 w-48 rounded-md border border-border bg-card p-1 shadow-lg z-50"
                >
                  <Link
                    href="/guarantee"
                    className={dropdownItemClass}
                    onClick={() => setAboutOpen(false)}
                  >
                    Score guarantee
                  </Link>
                  <Link
                    href="/proof"
                    className={dropdownItemClass}
                    onClick={() => setAboutOpen(false)}
                  >
                    Student results
                  </Link>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Mobile hamburger (below md) — collapses the primary links so the top
          bar fits a phone without overflowing (the overflow shoved the fixed
          Sage FAB + avatar off the right edge). Panel is fixed + full-width so
          it always sits within the viewport regardless of the button's x. */}
      <div className="relative md:hidden" ref={menuRef}>
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Open navigation menu"
          aria-expanded={menuOpen}
          aria-haspopup="true"
          className={`${linkClass} flex items-center`}
        >
          <Menu className="h-5 w-5" aria-hidden="true" />
        </button>
        {menuOpen && (
          <div
            data-nav-popover
            className="fixed inset-x-2 top-16 z-50 rounded-md border border-border bg-card p-1 shadow-lg"
          >
            {(actNav ? actMobileLinks : mobileLinks).map((l) => (
              <Link
                key={l.href}
                href={l.href}
                className={dropdownItemClass}
                onClick={() => setMenuOpen(false)}
              >
                {l.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>
  )
}
